import {
    CloudArrowUpIcon,
    DevicePhoneMobileIcon,
    EyeIcon,
    MapIcon,
    SparklesIcon,
    UserGroupIcon,
} from '@heroicons/react/24/outline'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'

import { cn } from '@/utils/cn'

import { MOCK_FAMILY } from './mockData'

const FAMILY_NAV = [
    { text: 'My Care Team', href: '/admin/family-care-team-preview/team', icon: UserGroupIcon },
    { text: 'Roadmap 2.0 (multi-source)', href: '/admin/family-care-team-preview/roadmap', icon: MapIcon },
    { text: 'Upload + AI Analysis', href: '/admin/family-care-team-preview/upload', icon: CloudArrowUpIcon },
]

const SCREENS = [
    { text: 'My Care Team', href: '/admin/family-care-team-preview/team' },
    { text: 'Add a provider', href: '/admin/family-care-team-preview/team/add' },
    { text: 'Provider detail', href: '/admin/family-care-team-preview/team/prov_002' },
    { text: 'Upload + AI analysis', href: '/admin/family-care-team-preview/upload' },
    { text: 'Roadmap 2.0 (multi-source)', href: '/admin/family-care-team-preview/roadmap' },
    { text: 'Conflicts list', href: '/admin/family-care-team-preview/conflicts' },
    { text: 'Conflict detail', href: '/admin/family-care-team-preview/conflicts/conf_001' },
    { text: 'Family-app preview', href: '/admin/family-care-team-preview/family-view' },
]

export default function FamilyCareTeamPreviewLayout() {
    const location = useLocation()
    const isStandalone = location.pathname.endsWith('/family-view')

    return (
        <div className='min-h-screen bg-neutral-50'>
            <PreviewBanner />

            {isStandalone ? (
                <div className='flex flex-col min-h-screen'>
                    <Outlet />
                </div>
            ) : (
                <div className='flex'>
                    {/* Sidebar */}
                    <aside className='hidden lg:flex flex-col w-64 min-h-screen bg-brand-600 sticky top-10'>
                        <div className='px-6 py-6 border-b border-white/10'>
                            <div className='flex items-center gap-3'>
                                <div className='w-10 h-10 bg-white rounded-lg flex items-center justify-center'>
                                    <SparklesIcon className='w-6 h-6 text-brand-600' />
                                </div>
                                <div className='flex flex-col'>
                                    <span className='text-xs uppercase tracking-wider text-white/60'>
                                        BrightPath
                                    </span>
                                    <span className='text-sm font-semibold text-white truncate'>
                                        {MOCK_FAMILY.primaryParent}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <nav className='flex-1 px-4 py-6'>
                            <ul className='space-y-1'>
                                {FAMILY_NAV.map((link) => (
                                    <li key={link.href}>
                                        <NavLink
                                            to={link.href}
                                            end
                                            className={({ isActive }) =>
                                                cn(
                                                    'flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors',
                                                    isActive
                                                        ? 'bg-white/15 text-white'
                                                        : 'text-white/80 hover:bg-white/10 hover:text-white'
                                                )
                                            }
                                        >
                                            <link.icon className='w-5 h-5 mr-3' />
                                            {link.text}
                                        </NavLink>
                                    </li>
                                ))}
                            </ul>
                        </nav>
                        <div className='px-4 py-4 border-t border-white/10'>
                            <Link
                                to='/admin/family-care-team-preview/family-view'
                                className='flex items-center gap-2 text-xs text-white/80 hover:text-white'
                            >
                                <DevicePhoneMobileIcon className='w-4 h-4' />
                                Family-app preview
                            </Link>
                            <div className='text-xs text-white/60 mt-3'>Signed in as</div>
                            <div className='text-sm text-white font-medium truncate'>
                                {MOCK_FAMILY.primaryParent}
                            </div>
                            <div className='text-xs text-white/70 truncate'>Parent of {MOCK_FAMILY.childFirstName}</div>
                        </div>
                    </aside>

                    <main className='flex-1 min-h-screen'>
                        <Outlet />
                    </main>
                </div>
            )}
        </div>
    )
}

function PreviewBanner() {
    return (
        <div className='sticky top-0 z-50 bg-amber-100 border-b border-amber-300 px-4 py-2'>
            <div className='flex flex-wrap items-center justify-between gap-2 max-w-7xl mx-auto'>
                <div className='flex items-center gap-2 text-xs sm:text-sm text-amber-900'>
                    <EyeIcon className='w-4 h-4 flex-shrink-0' />
                    <span className='font-semibold'>PREVIEW MODE</span>
                    <span className='hidden sm:inline'>
                        Family care-team mockup. Not connected to real data.
                    </span>
                </div>
                <div className='flex items-center gap-2'>
                    <ScreenJump />
                    <Link
                        to='/admin'
                        className='text-xs text-amber-900 underline hover:text-amber-700'
                    >
                        Exit to admin
                    </Link>
                </div>
            </div>
        </div>
    )
}

function ScreenJump() {
    const location = useLocation()
    return (
        <select
            value={location.pathname}
            onChange={(e) => {
                window.location.href = e.target.value
            }}
            className='text-xs bg-white border border-amber-300 rounded px-2 py-1 text-amber-900'
        >
            <option value='' disabled>
                Jump to screen...
            </option>
            {SCREENS.map((s) => (
                <option key={s.href} value={s.href}>
                    {s.text}
                </option>
            ))}
        </select>
    )
}
